import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { 
  isOnline, 
  getQueueLength, 
  syncQueuedMutations, 
  SyncResult,
  getOfflineQueue
} from '@/lib/offlineSync';
import { useToast } from '@/hooks/use-toast';

type SyncStatus = 'idle' | 'syncing' | 'success' | 'error';

interface OfflineContextType {
  isOnline: boolean;
  syncStatus: SyncStatus;
  queueLength: number;
  lastSyncResult: SyncResult | null;
  syncNow: () => Promise<void>;
  refreshQueueLength: () => void;
}

const OfflineContext = createContext<OfflineContextType | null>(null);

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
}

interface OfflineProviderProps {
  children: ReactNode;
}

export function OfflineProvider({ children }: OfflineProviderProps) {
  const [online, setOnline] = useState(isOnline());
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [queueLength, setQueueLength] = useState(0);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const { toast } = useToast();

  const refreshQueueLength = useCallback(() => {
    setQueueLength(getQueueLength());
  }, []);

  const syncNow = useCallback(async () => {
    if (!online || syncStatus === 'syncing') return;
    
    const queue = getOfflineQueue();
    if (queue.length === 0) return;

    setSyncStatus('syncing');
    
    try {
      const result = await syncQueuedMutations((current, total) => {
        console.log(`Syncing ${current}/${total}...`);
      });
      
      setLastSyncResult(result);
      refreshQueueLength();
      
      if (result.success && result.syncedCount > 0) {
        setSyncStatus('success');
      } else if (result.errors.length > 0) {
        // Check if errors are just delays (transient) vs actual failures
        const hasDelayedSync = result.errors.some(e => e.includes('Sync delayed'));
        const hasAuthError = result.errors.some(e => 
          e.includes('Not authenticated') || 
          e.includes('Authentication required') ||
          e.includes('log in')
        );
        const hasTransientError = result.errors.some(e => 
          e.includes('already in progress') ||
          e.includes('another tab') ||
          e.includes('network') ||
          e.includes('Failed to fetch') ||
          e.includes('NetworkError') ||
          e.includes('timeout')
        );
        const hasFailures = result.failedCount > 0;
        
        if (hasAuthError || hasTransientError) {
          // User not logged in or transient network issue - don't show error, just stay idle
          setSyncStatus('idle');
        } else if (hasDelayedSync && !hasFailures) {
          // Transient issue, items will retry automatically
          setSyncStatus('idle');
        } else {
          setSyncStatus('error');
          toast({
            title: 'Sync Issues',
            description: result.errors[0],
            variant: 'destructive',
          });
        }
      } else {
        setSyncStatus('idle');
      }
      
      setTimeout(() => setSyncStatus('idle'), 3000);
    } catch (error) {
      // Network errors are transient - don't show destructive toast
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isNetworkError = errorMessage.includes('fetch') || 
                             errorMessage.includes('network') || 
                             errorMessage.includes('Network') ||
                             errorMessage.includes('timeout');
      
      if (!isNetworkError) {
        setSyncStatus('error');
        toast({
          title: 'Sync Failed',
          description: 'Could not sync data. Will retry when connection is stable.',
          variant: 'destructive',
        });
      }
      setSyncStatus('idle');
    }
  }, [online, syncStatus, refreshQueueLength, toast]);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      toast({
        title: 'Back Online',
        description: 'Connection restored. Syncing your data...',
      });
      setTimeout(() => syncNow(), 1000);
    };

    const handleOffline = () => {
      setOnline(false);
      toast({
        title: 'You\'re Offline',
        description: 'Don\'t worry, your workouts are saved locally.',
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    refreshQueueLength();

    if (online && getQueueLength() > 0) {
      syncNow();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!online) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && online && getQueueLength() > 0) {
        syncNow();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [online, syncNow]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshQueueLength();
    }, 5000);
    return () => clearInterval(interval);
  }, [refreshQueueLength]);

  return (
    <OfflineContext.Provider
      value={{
        isOnline: online,
        syncStatus,
        queueLength,
        lastSyncResult,
        syncNow,
        refreshQueueLength,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
}
